// =====================================================================
// Assessment Center — Student Panel
// My Exams page — every attempt the student has started, resume
// in-progress ones, view results for submitted ones.
// =====================================================================
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Badge, Card, EmptyState, PageHeader, Spinner, statusTone } from "../../../shared/components/ui";
import { listMyAttempts, type MyAttempt } from "../lib/mock-api";

const examTypeLabel: Record<string, string> = {
  multiple_choice: "Multiple Choice",
  essay: "Essay",
  mixed: "Mixed",
};

const statusLabel: Record<string, string> = {
  not_started: "Not started",
  in_progress: "In progress",
  submitted: "Submitted — awaiting grading",
  graded: "Graded",
  expired: "Expired",
};

function formatDate(iso: string | null) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}

export default function MyExamsPage() {
  const [attempts, setAttempts] = useState<MyAttempt[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    listMyAttempts().then((data) => {
      setAttempts(data);
      setLoading(false);
    });
  }, []);

  return (
    <div>
      <PageHeader title="My Exams" subtitle="Everything you've started or submitted." />

      {loading ? (
        <div className="flex items-center gap-2 py-16 text-slate-500">
          <Spinner /> Loading your exams…
        </div>
      ) : attempts.length === 0 ? (
        <EmptyState
          title="You haven't started any exams yet"
          description="Head to Available Exams to open your first one."
        />
      ) : (
        <Card className="overflow-hidden">
          <table className="w-full text-left text-sm">
            <thead className="bg-navy-50/60 text-xs uppercase tracking-wide text-navy-700">
              <tr>
                <th className="px-4 py-3 font-medium">Exam</th>
                <th className="px-4 py-3 font-medium">Type</th>
                <th className="px-4 py-3 font-medium">Attempt</th>
                <th className="px-4 py-3 font-medium">Started</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {attempts.map((a) => (
                <tr key={a.id} className="border-t border-border-soft hover:bg-navy-50/30">
                  <td className="px-4 py-3 font-medium text-navy-900">{a.exam_title}</td>
                  <td className="px-4 py-3 text-slate-500">{examTypeLabel[a.exam_type]}</td>
                  <td className="px-4 py-3 text-slate-500">#{a.attempt_number}</td>
                  <td className="px-4 py-3 text-slate-500">{formatDate(a.started_at)}</td>
                  <td className="px-4 py-3">
                    <Badge tone={statusTone(a.status)}>{statusLabel[a.status]}</Badge>
                  </td>
                  <td className="px-4 py-3 text-right">
                    {a.status === "in_progress" ? (
                      <Link
                        to={`/student/assessment-center/exams/${a.id}`}
                        className="text-sm font-medium text-navy-700 hover:underline"
                      >
                        Resume
                      </Link>
                    ) : (
                      <Link
                        to={`/student/assessment-center/results/${a.id}`}
                        className="text-sm font-medium text-navy-700 hover:underline"
                      >
                        View
                      </Link>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
}
